import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Ear, Play, Pause, Square, X, Sparkles, MousePointerClick, HelpCircle, Repeat } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * ReadingAssistant — global floating helper for learners who can't yet
 * read the UI.
 *
 * Two modes:
 *   1. "Read this page" — walks the visible <main> element and reads its
 *      text out loud in order. Supports pause/resume/stop and a speed
 *      slider. Highlights the current sentence so the learner can follow
 *      along visually.
 *   2. "Tap to hear" — when on, clicking any text element on the page
 *      reads just that element (great for individual buttons/labels).
 *
 * Uses the browser Web Speech API (window.speechSynthesis) so it's:
 *   - Free (no server credits per read)
 *   - Instant (no round-trip)
 *   - Works offline once the PWA is installed
 *
 * The floating button is present on every page — including auth/marketing —
 * so a struggling reader can always self-serve.
 */

const READ_ATTR = "data-reading-target"; // marks the currently-spoken node
const IGNORE_SELECTOR = [
    "[data-reading-ignore]",
    "[data-testid='reading-assistant']",
    "script",
    "style",
    "noscript",
    "svg",
    "img",
    "input",
    "textarea",
    "code",
    "pre",
].join(",");

// Collect visible text chunks from the target element. Each chunk is a
// short-ish string (roughly one sentence or one heading/paragraph) so the
// TTS engine can pause naturally and highlight follows along cleanly.
function collectChunks(root) {
    if (!root) return [];
    const chunks = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (parent.closest(IGNORE_SELECTOR)) return NodeFilter.FILTER_REJECT;
            // Visibility check: getClientRects returns 0 for display:none / hidden.
            if (parent.getClientRects().length === 0) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });
    let node;
    while ((node = walker.nextNode())) {
        const text = node.textContent.replace(/\s+/g, " ").trim();
        if (text.length < 2) continue;
        // Split long paragraphs into ~sentences so the highlight moves in
        // digestible chunks.
        const sentences = text.match(/[^.!?]+[.!?]?/g) || [text];
        for (const s of sentences) {
            const t = s.trim();
            if (t) chunks.push({ text: t, el: node.parentElement });
        }
    }
    return chunks;
}

export default function ReadingAssistant() {
    const { user, refresh } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState("idle"); // idle | speaking | paused
    const [rate, setRate] = useState(1);
    const [tapToHear, setTapToHear] = useState(false);
    const [autoplay, setAutoplay] = useState(false);
    const [gameInstructions, setGameInstructions] = useState(null);
    const chunksRef = useRef([]);
    const idxRef = useRef(0);
    const rateRef = useRef(1);
    const lastTargetRef = useRef(null);

    const supported = typeof window !== "undefined" && "speechSynthesis" in window;

    // Sync autoplay preference from the authenticated user.
    useEffect(() => {
        if (user && typeof user.reading_autoplay === "boolean") {
            setAutoplay(user.reading_autoplay);
        }
    }, [user]);

    // Keep the latest rate value available inside long-lived event handlers.
    useEffect(() => {
        rateRef.current = rate;
    }, [rate]);

    // Clear any live utterance if the component unmounts (route change).
    useEffect(() => {
        return () => {
            if (supported) window.speechSynthesis.cancel();
            clearHighlight();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tap-to-hear mode: install a document-wide click listener while on.
    useEffect(() => {
        if (!tapToHear || !supported) return;
        const onClick = (e) => {
            const el = e.target && e.target.closest ? e.target.closest("p, h1, h2, h3, h4, li, button, a, label, span") : null;
            if (!el) return;
            if (el.closest(IGNORE_SELECTOR)) return;
            const txt = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
            if (!txt || txt.length < 2) return;
            e.preventDefault();
            e.stopPropagation();
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(txt);
            u.rate = rateRef.current;
            u.onstart = () => setHighlight(el);
            u.onend = () => clearHighlight();
            window.speechSynthesis.speak(u);
        };
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [tapToHear, supported]);

    // Detect a per-page "how to play" script: game pages set
    // <main data-game-instructions="…"> so the assistant can explain them
    // without the learner having to read anything. Re-check whenever the
    // route changes OR the tray opens (some routes finish rendering after
    // the initial location effect fires).
    useEffect(() => {
        const check = () => {
            const main = document.querySelector("main[data-game-instructions]");
            setGameInstructions(main ? main.getAttribute("data-game-instructions") : null);
        };
        // Try immediately + after a short settle so we catch both the
        // already-rendered case and the just-mounted case.
        check();
        const t = setTimeout(check, 120);
        return () => clearTimeout(t);
    }, [location.pathname, open]);

    // Autoplay: read the page automatically on every navigation when the
    // learner has that preference on. Small delay lets the DOM settle.
    useEffect(() => {
        if (!autoplay || !supported) return;
        // Cancel anything currently speaking from the previous route.
        window.speechSynthesis.cancel();
        clearHighlight();
        setState("idle");
        const t = setTimeout(() => {
            startReading();
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, autoplay]);

    const explainGame = () => {
        if (!supported || !gameInstructions) return;
        window.speechSynthesis.cancel();
        clearHighlight();
        // Break instructions into sentences so the highlight follows along
        // when the game surfaces the same text visibly. Each sentence can
        // OPTIONALLY contain `[[selector]]` anywhere in its text to bind
        // that sentence to a specific on-screen element — while it's being
        // spoken, we flash the matching element with a soft glow so learners
        // see what the sentence is referring to.
        const raw = gameInstructions.match(/[^.!?]+[.!?]?/g) || [gameInstructions];
        const flashBind = /%%([^%]+)%%/;
        chunksRef.current = raw
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => {
                const m = s.match(flashBind);
                if (m) {
                    const selector = m[1].trim();
                    const text = s.replace(flashBind, "").replace(/\s+/g, " ").trim();
                    const el = document.querySelector(selector);
                    return { text, el, mode: "flash" };
                }
                return { text: s, el: null };
            });
        idxRef.current = 0;
        setState("speaking");
        speakChunk(0);
    };

    const toggleAutoplay = async (next) => {
        setAutoplay(next);
        try {
            await api.put("/settings/reading-autoplay", { enabled: next });
            refresh?.();
        } catch {
            /* keep local state on failure; user can retry */
        }
        if (!next && supported) {
            window.speechSynthesis.cancel();
            clearHighlight();
            setState("idle");
        }
    };

    const setHighlight = (el, mode = "text") => {
        clearHighlight();
        if (!el) return;
        try {
            const attr = mode === "flash" ? "data-reading-flash" : READ_ATTR;
            el.setAttribute(attr, "true");
            lastTargetRef.current = { el, attr };
            // Scroll into view for flash-mode so the learner actually sees
            // the pulse. Text-mode already scrolls with the natural read.
            if (mode === "flash" && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        } catch { /* noop */ }
    };
    const clearHighlight = () => {
        const t = lastTargetRef.current;
        if (t && t.el) {
            try { t.el.removeAttribute(t.attr); } catch { /* noop */ }
        }
        lastTargetRef.current = null;
    };

    const speakChunk = (i) => {
        const chunks = chunksRef.current;
        if (i >= chunks.length) {
            setState("idle");
            clearHighlight();
            return;
        }
        idxRef.current = i;
        const chunk = chunks[i];
        const text = chunk.text;
        const el = chunk.el;
        const mode = chunk.mode || "text";
        const u = new SpeechSynthesisUtterance(text);
        u.rate = rateRef.current;
        u.onstart = () => setHighlight(el, mode);
        u.onend = () => speakChunk(i + 1);
        u.onerror = () => speakChunk(i + 1);
        window.speechSynthesis.speak(u);
    };

    const startReading = () => {
        if (!supported) return;
        window.speechSynthesis.cancel();
        const root = document.querySelector("main") || document.body;
        chunksRef.current = collectChunks(root);
        if (chunksRef.current.length === 0) return;
        setState("speaking");
        speakChunk(0);
    };

    const pause = () => {
        if (!supported) return;
        window.speechSynthesis.pause();
        setState("paused");
    };
    const resume = () => {
        if (!supported) return;
        window.speechSynthesis.resume();
        setState("speaking");
    };
    const stop = () => {
        if (!supported) return;
        window.speechSynthesis.cancel();
        clearHighlight();
        setState("idle");
    };

    if (!supported) return null;

    return (
        <>
            {/* Global CSS for the "currently being read" highlights */}
            <style>{`
                [${READ_ATTR}] {
                    background: linear-gradient(180deg, transparent 55%, #FCE9C6 55%);
                    border-radius: 4px;
                    padding: 0 3px;
                    transition: background 0.2s ease;
                }
                [data-reading-flash] {
                    animation: lur-reading-flash 1.15s ease-in-out infinite;
                    box-shadow: 0 0 0 4px rgba(212, 122, 85, 0.35),
                                0 0 0 8px rgba(212, 122, 85, 0.15);
                    border-radius: 12px;
                    position: relative;
                    z-index: 1;
                }
                @keyframes lur-reading-flash {
                    0%, 100% {
                        box-shadow: 0 0 0 4px rgba(212, 122, 85, 0.35),
                                    0 0 0 8px rgba(212, 122, 85, 0.15);
                    }
                    50% {
                        box-shadow: 0 0 0 6px rgba(212, 122, 85, 0.55),
                                    0 0 0 14px rgba(212, 122, 85, 0.20);
                    }
                }
            `}</style>

            {/* Floating pill launcher — always visible bottom-right */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-reading-ignore="true"
                    data-testid="reading-assistant-open"
                    aria-label="Open reading assistant"
                    className="fixed z-[65] bottom-6 right-4 md:right-6 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#2D5D4E] text-[#FAF9F6] shadow-lg hover:bg-[#1F4237] transition-all hover:-translate-y-0.5"
                >
                    <Ear className="w-4 h-4" strokeWidth={1.8} />
                    <span className="text-sm font-medium">Read to me</span>
                </button>
            )}

            {/* Expanded control tray */}
            {open && (
                <div
                    data-reading-ignore="true"
                    data-testid="reading-assistant"
                    className="fixed z-[65] bottom-6 right-4 md:right-6 w-[320px] bg-white rounded-2xl shadow-2xl border border-[#E5E7E2] overflow-hidden"
                    style={{ borderTop: "4px solid #2D5D4E" }}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7E2] bg-[#F5F1E4]">
                        <div className="flex items-center gap-2">
                            <Ear className="w-4 h-4 text-[#2D5D4E]" strokeWidth={1.8} />
                            <p className="font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                                Reading assistant
                            </p>
                        </div>
                        <button
                            onClick={() => { stop(); setOpen(false); }}
                            aria-label="Close"
                            data-testid="reading-assistant-close"
                            className="w-7 h-7 rounded-full flex items-center justify-center text-[#819888] hover:text-[#1C2321] hover:bg-white"
                        >
                            <X className="w-4 h-4" strokeWidth={1.8} />
                        </button>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* Main read/pause/stop controls */}
                        <div className="flex items-center gap-2">
                            {state === "idle" && (
                                <button
                                    onClick={startReading}
                                    data-testid="reading-assistant-play"
                                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                                >
                                    <Play className="w-4 h-4 fill-current" strokeWidth={1.8} /> Read this page
                                </button>
                            )}
                            {state === "speaking" && (
                                <button
                                    onClick={pause}
                                    data-testid="reading-assistant-pause"
                                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2]"
                                >
                                    <Pause className="w-4 h-4 fill-current" strokeWidth={1.8} /> Pause
                                </button>
                            )}
                            {state === "paused" && (
                                <button
                                    onClick={resume}
                                    data-testid="reading-assistant-resume"
                                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                                >
                                    <Play className="w-4 h-4 fill-current" strokeWidth={1.8} /> Resume
                                </button>
                            )}
                            {state !== "idle" && (
                                <button
                                    onClick={stop}
                                    aria-label="Stop"
                                    data-testid="reading-assistant-stop"
                                    className="w-10 h-10 rounded-lg border border-[#E5E7E2] bg-white text-[#B24C4C] hover:bg-[#FBE7DA] flex items-center justify-center"
                                >
                                    <Square className="w-4 h-4 fill-current" strokeWidth={1.8} />
                                </button>
                            )}
                        </div>

                        {/* How-to-play helper — only shown on game pages */}
                        {gameInstructions && (
                            <button
                                onClick={explainGame}
                                data-testid="reading-assistant-explain-game"
                                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#D47A55] bg-[#FDE9DD] text-[#B86644] font-medium hover:bg-[#FBD9C2]"
                            >
                                <HelpCircle className="w-4 h-4" strokeWidth={1.8} />
                                Explain how to play
                            </button>
                        )}

                        {/* Speed slider */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <p className="label-eyebrow">Speed</p>
                                <span className="text-sm text-[#515B57] font-medium" data-testid="reading-assistant-rate">
                                    {rate.toFixed(2)}×
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0.6"
                                max="1.4"
                                step="0.05"
                                value={rate}
                                onChange={(e) => setRate(parseFloat(e.target.value))}
                                data-testid="reading-assistant-speed"
                                className="w-full accent-[#2D5D4E]"
                                aria-label="Reading speed"
                            />
                            <div className="flex justify-between text-xs text-[#819888] mt-1">
                                <span>Slower</span>
                                <span>Faster</span>
                            </div>
                        </div>

                        {/* Tap-to-hear toggle */}
                        <label
                            className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[#E5E7E2] hover:bg-[#F5F1E4]"
                            data-testid="reading-assistant-tap-toggle-label"
                        >
                            <input
                                type="checkbox"
                                checked={tapToHear}
                                onChange={(e) => setTapToHear(e.target.checked)}
                                data-testid="reading-assistant-tap-toggle"
                                className="mt-1 accent-[#2D5D4E]"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-1.5">
                                    <MousePointerClick className="w-3.5 h-3.5 text-[#2D5D4E]" strokeWidth={1.8} />
                                    <p className="text-sm font-medium text-[#1C2321]">
                                        Tap any text to hear it
                                    </p>
                                </div>
                                <p className="text-xs text-[#819888] mt-0.5">
                                    Any button, label, or sentence you tap will be read aloud.
                                </p>
                            </div>
                        </label>

                        {/* Autoplay — read every new page automatically */}
                        <label
                            className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[#E5E7E2] hover:bg-[#F5F1E4]"
                            data-testid="reading-assistant-autoplay-label"
                        >
                            <input
                                type="checkbox"
                                checked={autoplay}
                                onChange={(e) => toggleAutoplay(e.target.checked)}
                                data-testid="reading-assistant-autoplay-toggle"
                                className="mt-1 accent-[#2D5D4E]"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-1.5">
                                    <Repeat className="w-3.5 h-3.5 text-[#2D5D4E]" strokeWidth={1.8} />
                                    <p className="text-sm font-medium text-[#1C2321]">
                                        Read every new page automatically
                                    </p>
                                </div>
                                <p className="text-xs text-[#819888] mt-0.5">
                                    Great if reading is still hard. Tap Stop any time.
                                </p>
                            </div>
                        </label>

                        <p className="text-xs text-[#819888] flex items-start gap-1.5">
                            <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2} />
                            <span>
                                This uses your device&apos;s voice — free and works offline.
                            </span>
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
